import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading; false = guest
  const [ready, setReady] = useState(false);

  const fetchMe = useCallback(async () => {
    // Always attempt /auth/me — auth can come from either the Bearer token in
    // localStorage (email/password login) or the httpOnly cookie (Google OAuth,
    // which never touches localStorage since the backend sets it directly and
    // redirects server-side). Gating this on localStorage presence would skip
    // the check entirely for anyone who just came back from Google login.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem("td_token");
      localStorage.removeItem("td_user");
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("td_token", data.token);
    localStorage.setItem("td_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name, extra = {}) => {
    const { data } = await api.post("/auth/register", { email, password, name, ...extra });
    localStorage.setItem("td_token", data.token);
    localStorage.setItem("td_user", JSON.stringify(data.user));
    setUser({ ...data.user, _new: true });
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    localStorage.removeItem("td_token");
    localStorage.removeItem("td_user");
    setUser(false);
  };

  const setSession = useCallback((token, userData) => {
    localStorage.setItem("td_token", token);
    localStorage.setItem("td_user", JSON.stringify(userData));
    setUser(userData);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, ready, login, register, logout, refresh: fetchMe, setSession }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
